// Shared Color Helper Functions
// Centralized status, severity, and priority color utilities
// ============================================================

// FRACAS Status Colors
export const getFracasStatusColor = (status: string) => {
    switch (status) {
        case 'Open':
            return 'bg-blue-100 text-blue-800';
        case 'Investigating':
            return 'bg-purple-100 text-purple-800';
        case 'RCA In Progress':
        case 'Analysis':
            return 'bg-indigo-100 text-indigo-800';
        case 'Corrective Action':
        case 'Action':
            return 'bg-green-100 text-green-800';
        case 'Verification':
            return 'bg-teal-100 text-teal-800';
        case 'Closed':
            return 'bg-gray-100 text-gray-800';
        default:
            return 'bg-gray-100 text-gray-800';
    }
};

// Lab Sample Status Colors
export const getLabStatusColor = (status: string) => {
    switch (status) {
        case 'received':
            return 'bg-blue-100 text-blue-800';
        case 'queued':
            return 'bg-yellow-100 text-yellow-800';
        case 'testing':
            return 'bg-purple-100 text-purple-800';
        case 'analysis':
            return 'bg-indigo-100 text-indigo-800';
        case 'complete':
            return 'bg-green-100 text-green-800';
        case 'on_hold':
            return 'bg-gray-100 text-gray-800';
        case 'cancelled':
            return 'bg-red-100 text-red-800';
        default:
            return 'bg-gray-100 text-gray-800';
    }
};

// Warranty Status Colors
export const getWarrantyStatusColor = (status: string) => {
    const colors: Record<string, string> = {
        'Submitted': 'bg-blue-100 text-blue-700',
        'Under Review': 'bg-yellow-100 text-yellow-700',
        'Approved': 'bg-green-100 text-green-700',
        'Denied': 'bg-red-100 text-red-700',
        'Resolved': 'bg-purple-100 text-purple-700',
        'Closed': 'bg-gray-100 text-gray-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
};

// Executive Cockpit Status Colors (for KPI indicators)
export const getKpiStatusColor = (status: string) => {
    switch (status) {
        case 'good': return 'bg-green-500';
        case 'warning': return 'bg-amber-500';
        case 'critical': return 'bg-red-500';
        default: return 'bg-gray-500';
    }
};

// FRACAS Severity Colors (with border)
export const getSeverityColor = (severity: string) => {
    switch (severity) {
        case 'Critical':
            return 'bg-red-100 text-red-800 border-red-300';
        case 'Major':
            return 'bg-orange-100 text-orange-800 border-orange-300';
        case 'Moderate':
        case 'Medium':
            return 'bg-amber-100 text-amber-800 border-amber-300';
        case 'Minor':
            return 'bg-yellow-100 text-yellow-800 border-yellow-300';
        default:
            return 'bg-gray-100 text-gray-800 border-gray-300';
    }
};

// MTBF Severity Colors (simpler variant)
export const getMtbfSeverityColor = (severity: string) => {
    switch (severity) {
        case 'Critical': return 'bg-red-100 text-red-700';
        case 'Major': return 'bg-yellow-100 text-yellow-700';
        case 'Minor': return 'bg-blue-100 text-blue-700';
        default: return 'bg-gray-100 text-gray-700';
    }
};

// Executive Alert Severity Colors (with left border for cards)
export const getAlertSeverityColor = (severity: string) => {
    switch (severity) {
        case 'high': return 'border-l-4 border-red-500 bg-red-50 dark:bg-red-900/20';
        case 'medium': return 'border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-900/20';
        default: return 'border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-900/20';
    }
};

// Priority Colors (Lab samples, tasks)
export const getPriorityColor = (priority: string) => {
    switch (priority) {
        case 'critical':
            return 'bg-red-100 text-red-800 border-red-300';
        case 'high':
            return 'bg-orange-100 text-orange-800 border-orange-300';
        case 'medium':
            return 'bg-amber-100 text-amber-800 border-amber-300';
        case 'low':
            return 'bg-yellow-100 text-yellow-800 border-yellow-300';
        default:
            return 'bg-gray-100 text-gray-800 border-gray-300';
    }
};

// Backward compatibility alias
export const getStatusColor = getFracasStatusColor;
